import type { SubscriptionTier } from "@/shared/lib/supabase/types"

export const ROUTES = {
  home: "/",
  explore: "/explore",
  chat: "/chat",
  chatWith: (id: string) => `/chat/${id}`,
  character: (id: string) => `/chat/${id}`,
  aiChat: "/ai-chat",
  create: "/create",
  generate: "/generate/image",
  settings: "/settings",
  settingsProfile: "/settings/profile",
  /** Public profile URL — pleasur.ai/@{username} (vanity URL, rewritten to /profile/:username internally) */
  profileByUsername: (username: string) => `/@${username}`,
  settingsAccount: "/settings/account",
  settingsPrivacy: "/settings/privacy",
  settingsSubscription: "/settings/subscription",
  login: "/login",
  register: "/register",
  forgotPassword: "/forgot-password",
  resetPassword: "/reset-password",
  subscription: "/subscription",
  affiliate: "/affiliate",
  support: "/support",
  blog: "/blog",
  blogPost: (slug: string) => `/blog/${slug}`,
  legal: "/legal",
} as const

/**
 * Resolve the base URL for auth callbacks.
 * On the client (browser), use window.location.origin — this always reflects
 * the real domain the user is on (e.g. https://pleasur.ai), regardless of what
 * NEXT_PUBLIC_APP_URL was baked into the bundle at build time.
 * On the server, fall back to NEXT_PUBLIC_APP_URL or localhost.
 */
function getBaseUrl(): string {
  if (typeof window !== "undefined") return window.location.origin
  return process.env.NEXT_PUBLIC_APP_URL?.replace(/\/$/, "") ?? "http://localhost:3000"
}

/** App-local OAuth callback URL. Exchanges the auth code server-side where the PKCE code verifier (stored in cookies by @supabase/ssr) is accessible. */
export function getAuthCallbackUrl(next?: string): string {
  const url = `${getBaseUrl()}/api/auth/callback`
  if (next) return `${url}?next=${encodeURIComponent(next)}`
  return url
}

/** Password-reset callback. Exchanges the PKCE code at `/api/auth/callback` and continues to `/reset-password`. Must be in Supabase Redirect URLs. */
export function getAppAuthCallbackUrl(): string {
  return `${getBaseUrl()}/api/auth/callback?next=${encodeURIComponent("/reset-password")}&type=recovery`
}

export const CHARACTER_CATEGORIES = [
  { value: "all", label: "All" },
  { value: "female", label: "Girls" },
  { value: "anime", label: "Anime" },
] as const

// ── Feature flags ─────────────────────────────────────────────────────────────
// Chat per-message "Listen" (TTS) control. Enabled now that xAI (Grok Voice) is
// the TTS vendor (replaces ElevenLabs).
export const VOICE_FEATURES_ENABLED = true

// Live phone-call feature (VAPI).
export const PHONE_CALL_FEATURE_ENABLED = true

// ── Credits / Subscription Constants ──────────────────────────────────────────
// Shared across features. Canonical source of truth for coin costs and tier helpers.

export const COIN_COSTS = {
  chat_image: 10,
  image_generation: 10,
  tts: 10,
  phone_call_per_minute: 50,
  video: 250,
  character_creation: 40,
} as const

export type CoinActionType = keyof typeof COIN_COSTS

export function isPaidTier(tier: SubscriptionTier): boolean {
  return tier !== "free"
}
